import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, X, Sparkles, Lightbulb } from 'lucide-react';
import { User } from '../../types';

interface AIAssistantProps {
  currentUser: User;
  deployments?: any[];
}

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  suggestions?: string[];
}

const AIAssistant: React.FC<AIAssistantProps> = ({ currentUser, deployments = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      addAIMessage(
        `👋 Hi ${currentUser.name}! I'm your AI Deployment Assistant.\n\nI can help you with:\n• Deployment guidance\n• Error troubleshooting\n• Best practices\n• Status summaries\n• Quick insights\n\nWhat can I help you with today?`,
        [
          "What's my deployment status?",
          "How do I create a deployment?",
          "Show me pending approvals",
          "Best practices for deployment"
        ]
      );
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addAIMessage = (content: string, suggestions?: string[]) => {
    const aiMessage: Message = {
      id: Date.now().toString(),
      type: 'ai',
      content,
      timestamp: new Date(),
      suggestions
    };
    setMessages(prev => [...prev, aiMessage]);
  };

  const addUserMessage = (content: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
  };

  const analyzeDeployments = () => {
    if (!deployments || deployments.length === 0) {
      return {
        total: 0,
        pending: 0,
        approved: 0,
        deployed: 0,
        failed: 0
      };
    }

    return {
      total: deployments.length,
      pending: deployments.filter(d => d.status === 'pending').length,
      tlApproved: deployments.filter(d => d.status === 'tl_approved').length,
      pmApproved: deployments.filter(d => d.status === 'pm_approved').length,
      deployed: deployments.filter(d => d.status === 'deployed').length,
      // Deployments never actually get a 'failed' status in this app's real
      // lifecycle (pending -> tl_approved -> pm_approved -> deployed/rejected,
      // with 'rolled_back' set if a deployed one is later rolled back) — this
      // branch previously always evaluated to 0 and was dead.
      failed: deployments.filter(d => d.status === 'rolled_back').length,
      rejected: deployments.filter(d => d.status === 'rejected').length
    };
  };

  const getAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    
    // Status queries
    if (input.includes('status') || input.includes('summary')) {
      const stats = analyzeDeployments();
      return `📊 **Your Deployment Overview:**\n\n` +
        `• Total Deployments: ${stats.total}\n` +
        `• Pending TL Approval: ${stats.pending}\n` +
        `• Pending PM Approval: ${stats.tlApproved}\n` +
        `• Ready to Deploy: ${stats.pmApproved}\n` +
        `• Deployed: ${stats.deployed}\n` +
        `• Rolled Back: ${stats.failed}\n` +
        `• Rejected: ${stats.rejected}\n\n` +
        (stats.pending > 0 ? `⚠️ You have ${stats.pending} deployment(s) awaiting TL approval.\n` : '') +
        (stats.failed > 0 ? `❌ You have ${stats.failed} rolled-back deployment(s) that need attention.\n` : '') +
        `\n💡 Tip: Keep your deployments organized and follow up on pending approvals!`;
    }

    // Create deployment
    if (input.includes('create') || input.includes('new deployment') || input.includes('submit')) {
      return `🚀 **Creating a New Deployment:**\n\n` +
        `**Steps:**\n` +
        `1. Click "Create Request" in the sidebar\n` +
        `2. Fill in deployment details:\n` +
        `   • Title (clear and descriptive)\n` +
        `   • Description (what's being deployed)\n` +
        `   • Priority (low/medium/high/critical)\n` +
        `   • Deployment type (bug/feature/payload/etc.)\n` +
        `   • Select modules\n` +
        `   • Choose institutes (if needed)\n` +
        `   • Add TL and PM approvers\n` +
        `   • Set scheduled date\n` +
        `3. Click Submit\n\n` +
        `✅ **Best Practices:**\n` +
        `• Use clear, specific titles\n` +
        `• Include detailed description\n` +
        `• Tag critical bugs as high priority\n` +
        `• Schedule during low-traffic hours`;
    }

    // Approval process
    if (input.includes('approval') || input.includes('approve')) {
      return `✅ **Approval Process Explained:**\n\n` +
        `**Two-Level Approval:**\n` +
        `1. **Team Lead (TL) Approval:**\n` +
        `   • Reviews technical aspects\n` +
        `   • Validates code quality\n` +
        `   • Checks dependencies\n\n` +
        `2. **Program Manager (PM) Approval:**\n` +
        `   • Reviews business impact\n` +
        `   • Books deployment slot\n` +
        `   • Validates scheduling\n\n` +
        `**Timeline:**\n` +
        `• TL approval: Usually within 2-4 hours\n` +
        `• PM approval: After all TLs approve\n` +
        `• Deployment: After PM approval\n\n` +
        `💡 Pro tip: Follow up with approvers if pending > 24 hours`;
    }

    // Best practices
    if (input.includes('best practice') || input.includes('tips') || input.includes('recommendation')) {
      return `💎 **Deployment Best Practices:**\n\n` +
        `**Before Deployment:**\n` +
        `✓ Test thoroughly in staging\n` +
        `✓ Have rollback plan ready\n` +
        `✓ Notify stakeholders\n` +
        `✓ Schedule during off-peak hours\n` +
        `✓ Document all changes\n\n` +
        `**During Deployment:**\n` +
        `✓ Monitor logs in real-time\n` +
        `✓ Watch error rates\n` +
        `✓ Check system health\n` +
        `✓ Keep communication open\n\n` +
        `**After Deployment:**\n` +
        `✓ Verify functionality\n` +
        `✓ Check performance metrics\n` +
        `✓ Update documentation\n` +
        `✓ Mark deployment as complete\n\n` +
        `🎯 Golden Rule: Always have a rollback strategy!`;
    }

    // Error handling
    if (input.includes('error') || input.includes('fail') || input.includes('problem') || input.includes('issue')) {
      return `🔧 **Troubleshooting Guide:**\n\n` +
        `**Common Issues:**\n\n` +
        `1. **Deployment Failed:**\n` +
        `   • Check logs for error details\n` +
        `   • Verify all dependencies\n` +
        `   • Check database connections\n` +
        `   • Review rollback options\n\n` +
        `2. **Approval Stuck:**\n` +
        `   • Contact approvers directly\n` +
        `   • Check if all info is complete\n` +
        `   • Verify module selections\n\n` +
        `3. **Slot Booking Issues:**\n` +
        `   • Check slot availability\n` +
        `   • Verify module conflicts\n` +
        `   • Try different time slot\n\n` +
        `4. **Permission Errors:**\n` +
        `   • Verify your role\n` +
        `   • Check module access\n` +
        `   • Contact admin if needed\n\n` +
        `💡 Still stuck? Use the Chat feature to reach out to your team!`;
    }

    // Slot booking
    if (input.includes('slot') || input.includes('book') || input.includes('schedule')) {
      return `📅 **Slot Booking Guide:**\n\n` +
        `**What is Slot Booking?**\n` +
        `Slot booking ensures deployments don't conflict and resources are available.\n\n` +
        `**How to Book:**\n` +
        `1. PM approves deployment\n` +
        `2. PM books a time slot\n` +
        `3. Select date and time window\n` +
        `4. Choose modules (prevents conflicts)\n` +
        `5. Confirm booking\n\n` +
        `**Slot Windows:**\n` +
        `• Slot 1: 09:00 - 10:30\n` +
        `• Slot 2: 11:00 - 13:00\n` +
        `• Slot 3: 14:00 - 15:30\n` +
        `• Slot 4: 16:00 - 17:00\n\n` +
        `⚠️ Same module can't be deployed twice in same day\n` +
        `💡 Pick the DevOps engineer carefully — anyone on leave or busy is greyed out`;
    }

    // Rollback
    if (input.includes('rollback') || input.includes('revert')) {
      return `🔄 **Rollback Guide:**\n\n` +
        `**When to Rollback:**\n` +
        `• Critical bugs in production\n` +
        `• Performance degradation\n` +
        `• Data corruption risks\n` +
        `• System instability\n\n` +
        `**How to Rollback:**\n` +
        `1. Open the deployed request (All Requests or Deployments)\n` +
        `2. Click the "Rollback" button on its card (DevOps/Admin)\n` +
        `3. Provide a reason (mandatory)\n` +
        `4. Confirm rollback\n` +
        `5. The requester is notified and its slot is released\n\n` +
        `**After Rollback:**\n` +
        `• Analyze what went wrong\n` +
        `• Fix the issue\n` +
        `• Test thoroughly\n` +
        `• Redeploy when ready\n\n` +
        `⚠️ Rollbacks are logged and tracked for audit purposes`;
    }

    // Role-specific help
    if (input.includes('role') || input.includes('permission')) {
      return `👥 **Role-Based Access:**\n\n` +
        `**Your Role: ${currentUser.role.toUpperCase()}**\n\n` +
        (currentUser.role === 'developer' ? 
          `**Developer Permissions:**\n` +
          `✓ Create deployment requests\n` +
          `✓ View own deployments\n` +
          `✓ Edit pending requests\n` +
          `✓ View deployment history\n\n` :
        currentUser.role === 'tl' ?
          `**Team Lead Permissions:**\n` +
          `✓ All developer permissions\n` +
          `✓ Approve/reject requests\n` +
          `✓ View team deployments\n` +
          `✓ Technical review\n` +
          `✓ Assign modules\n\n` :
        currentUser.role === 'pm' ?
          `**Program Manager Permissions:**\n` +
          `✓ All TL permissions\n` +
          `✓ Final approval authority\n` +
          `✓ Book deployment slots\n` +
          `✓ View all deployments\n` +
          `✓ Business validation\n\n` :
        currentUser.role === 'devops' ?
          `**DevOps Permissions:**\n` +
          `✓ Execute deployments\n` +
          `✓ View deployment status\n` +
          `✓ Manage rollbacks\n` +
          `✓ Access deployment logs\n` +
          `✓ System administration\n\n` :
          `**Admin Permissions:**\n` +
          `✓ Full system access\n` +
          `✓ User management\n` +
          `✓ Module management\n` +
          `✓ Institute management\n` +
          `✓ Reports and analytics\n\n`) +
        `💡 Contact admin to request role changes`;
    }

    // Default response with context awareness
    return `🤔 I can help you with:\n\n` +
      `• **"What's my status?"** - View your deployment overview\n` +
      `• **"How do I create a deployment?"** - Step-by-step guide\n` +
      `• **"Explain approvals"** - Understanding the process\n` +
      `• **"Best practices"** - Deployment recommendations\n` +
      `• **"Troubleshoot errors"** - Common issues & fixes\n` +
      `• **"How do slots work?"** - Slot booking guide\n` +
      `• **"How to rollback?"** - Rollback process\n\n` +
      `Try asking me any of these questions! 💡`;
  };

  // Accepts explicit text so suggestion chips can send what was clicked
  // (state set via setInput isn't visible until the next render).
  const handleSendMessage = (text?: string) => {
    const userQuery = (text ?? input).trim();
    if (!userQuery) return;

    addUserMessage(userQuery);
    setInput('');
    setIsTyping(true);

    // Simulate AI thinking time
    setTimeout(() => {
      const response = getAIResponse(userQuery);
      setIsTyping(false);
      
      // Determine suggestions based on context
      let suggestions: string[] = [];
      const lowerInput = userQuery.toLowerCase();
      
      if (lowerInput.includes('status')) {
        suggestions = ["Show pending approvals", "What deployments failed?", "Best practices"];
      } else if (lowerInput.includes('create') || lowerInput.includes('deployment')) {
        suggestions = ["How do slots work?", "Explain approvals", "Best practices"];
      } else if (lowerInput.includes('error') || lowerInput.includes('fail')) {
        suggestions = ["How to rollback?", "What's my status?", "Contact support"];
      } else {
        suggestions = ["What's my status?", "How do I create?", "Best practices"];
      }
      
      addAIMessage(response, suggestions);
    }, 800);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 z-50 group"
      >
        <Bot className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 rounded-full animate-pulse"></span>
        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block">
          <div className="bg-gray-900 text-white text-sm rounded-lg px-3 py-2 whitespace-nowrap">
            Ask AI Assistant 🤖
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bot className="h-6 w-6" />
            <Sparkles className="h-3 w-3 absolute -top-1 -right-1 text-yellow-300" />
          </div>
          <div>
            <h3 className="font-semibold">AI Assistant</h3>
            <p className="text-xs text-purple-100">Always here to help</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-white/20 p-1 rounded transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message) => (
          <div key={message.id}>
            <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-2xl p-3 ${
                  message.type === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-800 shadow-md'
                }`}
              >
                <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                <div className={`text-xs mt-1 ${message.type === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
            
            {/* Suggestions */}
            {message.type === 'ai' && message.suggestions && message.suggestions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {message.suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-xs bg-white border border-purple-200 text-purple-600 px-3 py-1.5 rounded-full hover:bg-purple-50 transition-colors flex items-center gap-1"
                  >
                    <Lightbulb className="h-3 w-3" />
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl p-3 shadow-md">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Ask me anything..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!input.trim()}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-2 rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-center gap-1 text-xs text-gray-400">
          <Sparkles className="h-3 w-3" />
          <span>Powered by AI Intelligence</span>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
